import type { BrowseFilterState } from "@nook/client-ui"
import { useSyncExternalStore } from "react"

export { browseFilters, DATE_PRESETS, dayStart, localDay, matchPreset, presetRange, type DatePreset } from "@nook/client-ui"

/**
 * The library's filters and view (block 19).
 *
 * Filters live in `/`'s search params so a filtered library reloads and shares
 * as itself. Days stay `YYYY-MM-DD` there and become this browser's local
 * midnight only on the way to the API — a bare date would be UTC's day, not
 * the reader's.
 */

export interface BrowseSearch extends BrowseFilterState {
  /** The nook open in the modal over the library. */
  nook?: string | undefined
}

const day = (value: unknown) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined

export function parseBrowseSearch(search: Record<string, unknown>): BrowseSearch {
  const site = (Array.isArray(search.site) ? search.site : []).filter((host) => typeof host === "string")

  return {
    site: site.length ? site : undefined,
    from: day(search.from),
    to: day(search.to),
    nook: typeof search.nook === "string" ? search.nook : undefined,
  }
}

export type LibraryView = "grid" | "compact"

const VIEW_KEY = "nook:library-view"

const listeners = new Set<() => void>()

/**
 * Grid or Compact — a preference, not a filter,
 * so not in the URL. One store, because the header sets it and the library reads it.
 */
export function useLibraryView() {
  const view = useSyncExternalStore(
    (listener) => (listeners.add(listener), () => listeners.delete(listener)),
    (): LibraryView => (localStorage.getItem(VIEW_KEY) === "compact" ? "compact" : "grid"),
  )

  return [
    view,
    (next: LibraryView) => {
      localStorage.setItem(VIEW_KEY, next)
      listeners.forEach((listener) => listener())
    },
  ] as const
}
